/**
 * Which group members are hidden from the grid.
 *
 * On a 97-member group, most of the value is in narrowing it down to the few
 * people you actually care about. There is no endpoint to store that, so the
 * choice lives in this browser — like the row density and the folded sidebar.
 *
 * Keyed by account uuid, which is what the events carry.
 */

import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'beeplanif.hiddenMembers';

function readStored() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return new Set(Array.isArray(raw) ? raw : []);
  } catch {
    // Private browsing, or a value someone else wrote: start from nothing.
    return new Set();
  }
}

export function useHiddenMembers() {
  const [hidden, setHidden] = useState(readStored);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...hidden]));
    } catch {
      // Not persisting is survivable; the session still honours the choice.
    }
  }, [hidden]);

  const toggleMember = useCallback((uuid) => {
    setHidden((current) => {
      const next = new Set(current);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }, []);

  /** Show or hide every member of a group at once. */
  const setGroupHidden = useCallback((uuids, shouldHide) => {
    setHidden((current) => {
      const next = new Set(current);
      for (const uuid of uuids) {
        if (shouldHide) next.add(uuid);
        else next.delete(uuid);
      }
      return next;
    });
  }, []);

  return { hidden, toggleMember, setGroupHidden };
}
